import { useState, useMemo } from 'react';
import { stateFormsData } from '../../../mocks/taxForms';

export default function StateResources() {
  const [selectedState, setSelectedState] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const filteredStates = useMemo(() => {
    if (!searchTerm) return stateFormsData;
    return stateFormsData.filter((state) =>
      state.state.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  const selectedStateData = stateFormsData.find(
    (state) => state.state === selectedState
  );

  return (
    <section className="py-20 bg-white" id="state-resources" aria-labelledby="state-resources-heading">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 id="state-resources-heading" className="text-4xl font-bold text-gray-900 mb-4">
            <strong>State Tax Resources</strong>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Access state-specific tax forms, rates, and official government resources for all 50 states
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <label className="block text-lg font-semibold text-gray-900 mb-4">
              Select Your State
            </label>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full px-6 py-4 border-2 border-gray-200 rounded-xl text-lg focus:border-teal-500 focus:outline-none transition-colors cursor-pointer"
            >
              {stateFormsData.map((state) => (
                <option key={state.state} value={state.state}>
                  {state.state}
                </option>
              ))}
            </select>
          </div>

          {selectedStateData && (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-start gap-4 mb-8 pb-8 border-b border-gray-200">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex-shrink-0">
                  <i className="ri-government-line text-3xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">{selectedStateData.state}</h3>
                  <p className="text-lg text-gray-700 font-medium mb-2">{selectedStateData.agency}</p>
                  <a
                    href={selectedStateData.agencyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer"
                  >
                    Visit Agency Website
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>

              <div>
                <h4 className="text-2xl font-bold text-gray-900 mb-6">Available Tax Forms</h4>
                <div className="space-y-4">
                  {selectedStateData.forms.map((form, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-1">
                        <h5 className="text-xl font-bold text-gray-900 mb-2">{form.name}</h5>
                        <p className="text-gray-600">{form.description}</p>
                      </div>
                      {form.pdfUrl && (
                        <a
                          href={form.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-6 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                        >
                          <i className="ri-download-line"></i>
                          Download PDF
                        </a>
                      )}
                      {!form.pdfUrl && (
                        <div className="ml-6 px-6 py-3 bg-gray-300 text-gray-600 rounded-lg font-semibold whitespace-nowrap inline-flex items-center gap-2">
                          <i className="ri-information-line"></i>
                          No Forms Required
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 p-6 bg-teal-50 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
                    <i className="ri-information-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h5 className="text-lg font-bold text-gray-900 mb-2">Federal Integration</h5>
                    <p className="text-gray-700 leading-relaxed">
                      State tax returns are typically filed alongside federal returns. Most states use your federal adjusted gross income (AGI) as a starting point. Make sure to complete your federal Form 1040 before filing your state return.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-coral-500 to-coral-600 rounded-xl mb-6">
              <i className="ri-file-list-3-line text-2xl text-white"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-3">All 50 States</h4>
            <p className="text-gray-600 leading-relaxed">
              Complete coverage of tax forms and resources for every US state and territory
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl mb-6">
              <i className="ri-links-line text-2xl text-white"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-3">Direct Links</h4>
            <p className="text-gray-600 leading-relaxed">
              Download forms directly from official state revenue department websites
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl mb-6">
              <i className="ri-refresh-line text-2xl text-white"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-3">Always Updated</h4>
            <p className="text-gray-600 leading-relaxed">
              Links point to current year forms maintained by state tax authorities
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}